import {
  CheckCircleOutline,
  EditNote,
  GppBad,
  PanTool,
  Person,
  School,
  Send,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ICriticalAlertPayload, SeverityLevel } from 'services/OccurrenceService/types';

interface CriticalAlertModalProps {
  open: boolean;
  onClose: () => void;
  onRegisterAction: (actionText: string) => Promise<void>;
  isLoadingAction?: boolean;
  alertData?: ICriticalAlertPayload | null;
}

export const CriticalAlertModal: React.FC<CriticalAlertModalProps> = ({
  open,
  onClose,
  onRegisterAction,
  isLoadingAction = false,
  alertData,
}) => {
  const [showActionInput, setShowActionInput] = useState(false);
  const [actionText, setActionText] = useState('');

  useEffect(() => {
    if (!open) {
      setShowActionInput(false);
      setActionText('');
    }
  }, [open, alertData?.occurrenceId]);

  if (!alertData) return null;

  const getSeverityLabel = (severity?: string | SeverityLevel) => {
    if (!severity) return 'CRÍTICA';

    const sev = String(severity).toLowerCase();

    switch (sev) {
      case 'critical':
      case '1':
        return 'CRÍTICA';
      case 'high':
      case '2':
        return 'ALTA';
      case 'medium':
      case '3':
        return 'MÉDIA';
      case 'informative':
      case '4':
        return 'INFORMATIVA';
      default:
        return String(severity).toUpperCase();
    }
  };

  const handleSubmitAction = async () => {
    const trimmed = actionText.trim();
    if (!trimmed || isLoadingAction) return;

    try {
      await onRegisterAction(trimmed);
      setActionText('');
      setShowActionInput(false);
      onClose();
    } catch {
      // Falha gerenciada pelo mutation/toast
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(185, 28, 28, 0.5)',
            backdropFilter: 'blur(6px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: '#0f0a0b',
          border: '2px solid #ef4444',
          boxShadow: '0 0 50px rgba(239, 68, 68, 0.4)',
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* 1. CABEÇALHO DE ALERTA MÁXIMO */}
      <Box sx={{ bgcolor: '#ef4444', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: '#ffffff',
            color: '#ef4444',
            width: 48,
            height: 48,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <PanTool sx={{ fontSize: 28 }} />
        </Avatar>
        <Box sx={{ color: '#ffffff' }}>
          <Typography
            variant="caption"
            fontWeight="900"
            letterSpacing={1.2}
            textTransform="uppercase"
            display="block"
            sx={{ opacity: 0.9 }}
          >
            ALERTA CRÍTICO • CATRACA TRAVADA
          </Typography>
          <Typography
            variant="h6"
            fontWeight="black"
            lineHeight={1.1}
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            ACESSO BLOQUEADO PELA PORTARIA
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3, mt: 0.5 }}>
        {/* TÍTULO DA OCORRÊNCIA */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <GppBad sx={{ color: '#ef4444', fontSize: 24 }} />
          <Typography variant="subtitle1" fontWeight="800" color="#ef4444" letterSpacing={0.5}>
            {alertData.title}
          </Typography>
        </Box>

        {/* INSTRUÇÕES OPERACIONAIS */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Typography variant="body2" color="#fca5a5" fontWeight="500" lineHeight={1.6}>
            {alertData.description ||
              'Indivíduo com restrição cadastrada. Verifique o procedimento operacional antes de liberar o acesso.'}
          </Typography>
        </Box>

        {/* 2. FOTO DE RECONHECIMENTO FACIAL E DADOS */}
        <Box sx={{ bgcolor: '#181113', p: 2.5, borderRadius: 2.5, border: '1px solid #3f2024', mb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
            textTransform="uppercase"
            display="block"
            mb={1.5}
          >
            Detecção no Leitor ({alertData.timestamp}):
          </Typography>

          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box
              sx={{
                position: 'relative',
                width: 84,
                height: 84,
                flexShrink: 0,
                borderRadius: 2,
                p: 0.5,
                border: '2px solid #ef4444',
                bgcolor: '#2a1518',
                boxShadow: '0 0 15px rgba(239, 68, 68, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Avatar
                src={alertData.photoUrl}
                variant="rounded"
                sx={{ width: '100%', height: '100%', borderRadius: 1.5, bgcolor: '#3f2024' }}
              >
                <Person sx={{ fontSize: 40, color: '#fca5a5' }} />
              </Avatar>
            </Box>

            {/* DADOS DE IDENTIFICAÇÃO */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight="bold" color="text.primary" noWrap lineHeight={1.2}>
                {alertData.personName}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                CPF: <strong style={{ color: '#e2e8f0' }}>{alertData.personCpf || 'Não informado'}</strong>
              </Typography>

              <Chip
                label={`RESTRIÇÃO ${getSeverityLabel(alertData.severity)}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                  fontWeight: '800',
                  fontSize: '0.65rem',
                  height: 22,
                  borderRadius: 1,
                }}
              />
            </Box>
          </Stack>

          {/* VÍNCULO COM O ALUNO */}
          {alertData.relationInfo && (
            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid #2f1a1d',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <School sx={{ color: '#f97316', fontSize: 18 }} />
              <Typography variant="body2" color="#fdba74" fontWeight="bold" noWrap>
                Vínculo: {alertData.relationInfo}
              </Typography>
            </Box>
          )}
        </Box>

        {/* 3. REGISTRO RÁPIDO DE AUDITORIA */}
        {showActionInput && (
          <Box sx={{ mt: 2.5, p: 2, bgcolor: '#1a1012', borderRadius: 2, border: '1px solid #f97316' }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="#fdba74"
              textTransform="uppercase"
              display="block"
              mb={1}
            >
              📝 Registrar Providência (Auditoria da Portaria):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Ex: Acionada a Patrulha Escolar (190). Aluno mantido na sala da coordenação. Mãe comunicada..."
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              disabled={isLoadingAction}
              autoFocus
              slotProps={{
                input: {
                  sx: { bgcolor: '#0a0607', color: 'text.primary', borderRadius: 1.5, fontSize: '13px' },
                },
              }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.5}>
              <Button
                size="small"
                onClick={() => setShowActionInput(false)}
                disabled={isLoadingAction}
                sx={{ color: 'text.secondary' }}
              >
                Cancelar
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSubmitAction}
                disabled={isLoadingAction || !actionText.trim()}
                startIcon={
                  isLoadingAction ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Send sx={{ fontSize: 16 }} />
                  )
                }
                sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' }, fontWeight: 'bold' }}
              >
                {isLoadingAction ? 'Salvando...' : 'Salvar Histórico'}
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>

      {/* 4. AÇÕES DO RODAPÉ */}
      <DialogActions
        sx={{
          p: 2.5,
          bgcolor: '#140c0e',
          borderTop: '1px solid #2f1a1d',
          display: 'flex',
          gap: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          onClick={() => setShowActionInput(!showActionInput)}
          disabled={isLoadingAction}
          startIcon={<EditNote />}
          sx={{
            flex: 1,
            borderColor: '#3f2024',
            color: '#e2e8f0',
            bgcolor: '#1c1114',
            fontWeight: '600',
            py: 1,
            '&:hover': { bgcolor: '#2a161a', borderColor: '#f97316', color: '#fdba74' },
          }}
        >
          {showActionInput ? 'Ocultar' : 'Registrar Ação'}
        </Button>

        <Button
          variant="contained"
          onClick={onClose}
          disabled={isLoadingAction}
          startIcon={<CheckCircleOutline />}
          sx={{
            flex: 1.2,
            bgcolor: '#ef4444',
            color: '#ffffff',
            fontWeight: 'bold',
            py: 1,
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            '&:hover': { bgcolor: '#dc2626' },
          }}
        >
          Confirmar Ciência
        </Button>
      </DialogActions>
    </Dialog>
  );
};